import {keepPreviousData, useQuery} from '@tanstack/react-query';

import {splitImageOrderBy} from '~utils/miscUtils';
import {ImageFilters, ImageOrderBy} from '~redux/types/images';
import {Model} from '~redux/types/models';
import {selectMetadata} from '~redux/reducers/filtersReducer';
import {selectItemsPerPage} from '~redux/reducers/imageReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {useImageFilters} from '~components/images/image-overview/imageOverview.hooks';
import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';
import {useLabelIds, useLabelItems} from '~components/models/model.hooks';
import {useLabelInstances} from 'src/contexts/LabelInstancesContext';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ImageFiltersPayload, ImagesDataPayload, SortAndCountArgs} from './images';
import {getLabelInstancesData, getLabelInstancesIds} from './label-instances';

type LabelInstancesPayload = ImagesDataPayload;

export const LabelInstancesQueryKeys = {
  all: ['labelInstances'] as const,
  labelInstancesDataFilterPaginated: (
    projectId: string,
    filters: ImageFiltersPayload | undefined,
    sorting: SortAndCountArgs | undefined,
    targetPage: number | undefined,
    mode: ImageOverviewMode | undefined,
  ) => [...LabelInstancesQueryKeys.all, projectId, filters, sorting, targetPage, mode] as const,
  labelInstancesExistence: (
    projectId: string,
    filters: ImageFiltersPayload | undefined,
    mode: ImageOverviewMode | undefined,
  ) => [...LabelInstancesQueryKeys.all, projectId, 'existence', filters, mode] as const,
  labelInstancesIds: (projectId: string, filters: ImageFiltersPayload) =>
    [...LabelInstancesQueryKeys.all, projectId, filters] as const,
};

interface LabelInstancesQueryOptions {
  mode?: ImageOverviewMode;
  enabled?: boolean;
  pagination?: {
    count: number;
  };
  model?: Model;
}

export function useLabelInstancesQuery(options: LabelInstancesQueryOptions = {}) {
  const {mode = 'model-defect-book', enabled = true, pagination, model} = options;

  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  const itemsPerPage = useAppSelector(selectItemsPerPage);
  const labelIds = useLabelIds({from: 'current-model-or-project', model});
  const modelLabelItems = useLabelItems({from: 'current-model', model});
  const [imageFilters] = useImageFilters(mode, modelLabelItems);
  const areMachinePredictions = mode === 'archive';
  const {currentPage, currentPagination} = useLabelInstances();
  const {currentModel} = useCurrentModel();

  const isModelDefectBook = mode === 'model-defect-book';

  // The orderBy is not part of the filters, so we need to remove it from the filters object
  // before passing it to the query. [https://maddoxai.atlassian.net/browse/PD-3763]
  const {orderBy, ...filters} = imageFilters || {};

  const payload: LabelInstancesPayload = {
    filters: {...filters, ...(isModelDefectBook && {modelId: model?.id || currentModel?.id})},
    pagination: {
      ...splitImageOrderBy(orderBy),
      action: currentPagination,
      count: pagination?.count ?? itemsPerPage,
    },
  };

  // for archive mode (machine predictions), allow query to run even without labels
  // to properly show empty state for new projects without annotations.
  // for model-defect-book mode, we still need labels since it requires a model context.
  const hasLabelsOrIsArchiveMode = areMachinePredictions || labelIds?.length > 0;

  return useQuery({
    queryKey: LabelInstancesQueryKeys.labelInstancesDataFilterPaginated(
      projectId,
      payload.filters,
      {sortBy: payload.pagination?.sortBy, sortOrder: payload.pagination?.sortOrder, count: payload.pagination?.count},
      currentPage,
      mode,
    ),
    // Deliberately not wired to the global loading indicator: `ImagesGrid` ORs that counter into the image view's
    // `isLoading`, and a slow instance-view request still in flight after switching back to the image view would
    // hide the already-loaded thumbnails behind skeletons. The instance grid derives its own skeletons from
    // `labelInstancesQuery.isLoading`.
    queryFn: ({signal}) => {
      const defaultSortedPayload = payload?.pagination?.sortBy
        ? payload
        : {
            ...payload,
            pagination: {...payload.pagination, ...splitImageOrderBy(ImageOrderBy.TIMESTAMP_DESC)},
          };
      return getLabelInstancesData(projectId, defaultSortedPayload, areMachinePredictions, signal, metadata);
    },
    enabled: hasLabelsOrIsArchiveMode && enabled,
  });
}

export function useLabelInstancesExistenceQuery(
  options: {mode?: ImageOverviewMode; enabled?: boolean; model?: Model} = {},
) {
  const {mode = 'model-defect-book', enabled = true, model} = options;

  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);
  const labelIds = useLabelIds({from: 'current-model-or-project', model});
  const modelLabelItems = useLabelItems({from: 'current-model', model});
  const [imageFilters] = useImageFilters(mode, modelLabelItems);
  const areMachinePredictions = mode === 'archive';
  const {currentModel} = useCurrentModel();

  const isModelDefectBook = mode === 'model-defect-book';

  // The orderBy is not part of the filters, so we need to remove it from the filters object
  // before passing it to the query. [https://maddoxai.atlassian.net/browse/PD-3763]
  const {orderBy: _orderBy, ...filters} = imageFilters || {};

  // We only care whether any label-instances exist, so:
  // - no pagination action
  // - count=1
  // - always use stable default sort
  const payload: LabelInstancesPayload = {
    filters: {...filters, ...(isModelDefectBook && {modelId: model?.id || currentModel?.id})},
    pagination: {
      action: undefined,
      count: 1,
      ...splitImageOrderBy(ImageOrderBy.TIMESTAMP_DESC),
    },
  };

  // for archive mode (machine predictions), allow query to run even without labels
  // to properly show empty state for new projects without annotations.
  // for model-defect-book mode, we still need labels since it requires a model context.
  const hasLabelsOrIsArchiveMode = areMachinePredictions || labelIds?.length > 0;

  // This probe only decides whether the instance-view toggle is available, so it must not touch the global
  // loading indicator: `ImagesGrid` ORs that counter into the gallery's `isLoading`, which used to replace the
  // already-loaded thumbnails with skeletons until `/label_instances/{projectId}/machine` had answered.
  // `keepPreviousData` keeps the last known answer while the filters change, so the toggle does not flip back
  // to "unknown" (and the user is not bounced out of the instance view) on every refetch.
  return useQuery({
    queryKey: LabelInstancesQueryKeys.labelInstancesExistence(projectId, payload.filters, mode),
    queryFn: ({signal}) => getLabelInstancesData(projectId, payload, areMachinePredictions, signal, metadata),
    enabled: hasLabelsOrIsArchiveMode && enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * This query returns the label instance ids for given filters.
 * Converts frontend orderBy field to pagination.sortBy/sortOrder for the API.
 */
export function useLabelInstancesIds(labelInstanceFilters: ImageFilters | undefined, enabled = true) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const metadata = useAppSelector(selectMetadata);

  // Extract orderBy from filters and convert to pagination format
  const {orderBy, ...filters} = labelInstanceFilters || {};
  const pagination = orderBy ? splitImageOrderBy(orderBy) : undefined;

  return useQuery({
    queryKey: LabelInstancesQueryKeys.labelInstancesIds(projectId, labelInstanceFilters ?? {}),
    queryFn: ({signal}) => getLabelInstancesIds(projectId, filters, pagination, signal, metadata),
    enabled,
  });
}
